import { useLayoutEffect, useRef, useState } from 'react'

const SENTINEL_ID = 'preview-sentinel'
const PADDING = 20
const LEFT_OFFSET = 30

export const usePreviewTitleAlignment = ({
  isArticlePreview,
  postId,
}: {
  isArticlePreview: boolean
  postId: string
}) => {
  const [position, setPosition] = useState<{ left: number; maxWidth: number } | null>(null)
  const leftBoundaryRef = useRef<HTMLDivElement>(null)
  const rightBoundaryRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    let rafId: number | null = null
    let attempts = 0
    const maxAttempts = 10

    const measure = () => {
      const sentinel = document.getElementById(SENTINEL_ID)
      const leftBoundary = leftBoundaryRef.current
      const rightBoundary = rightBoundaryRef.current
      const content = contentRef.current

      if (sentinel && leftBoundary && rightBoundary && content) {
        const sentinelRect = sentinel.getBoundingClientRect()
        const leftBoundaryRect = leftBoundary.getBoundingClientRect()
        const rightBoundaryRect = rightBoundary.getBoundingClientRect()
        const contentRect = content.getBoundingClientRect()

        const minLeft = leftBoundaryRect.right + PADDING
        const maxRight = rightBoundaryRect.left - PADDING
        const maxWidth = maxRight - minLeft
        const offset = isArticlePreview ? 0 : LEFT_OFFSET

        const sentinelCenter = sentinelRect.left + sentinelRect.width / 2
        const contentWidth = Math.min(contentRect.width, maxWidth)
        const idealLeft = sentinelCenter - contentWidth / 2 + offset
        const clampedLeft = Math.max(minLeft, Math.min(idealLeft, maxRight - contentWidth))

        setPosition({ left: clampedLeft, maxWidth })
        return true
      }
      return false
    }

    const tryMeasure = () => {
      if (!measure() && attempts < maxAttempts) {
        attempts++
        rafId = requestAnimationFrame(tryMeasure)
      }
    }

    tryMeasure()

    window.addEventListener('resize', measure)
    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId)
      window.removeEventListener('resize', measure)
    }
  }, [isArticlePreview, postId])

  return { leftBoundaryRef, rightBoundaryRef, contentRef, position }
}
